import * as React from 'react';
import { observer } from 'mobx-react';
import { EditorContext } from '@tiptap/react';

import AttachFileIcon from '@mui/icons-material/AttachFile';

import { useTiptapEditor } from 'containers/Tiptap/hooks/use-tiptap-editor';
import { useHeaderFooterOverlayEditor } from 'containers/Tiptap/hooks/use-header-footer-overlay-editor';
import { useEditorStore } from 'containers/Tiptap/hooks/useEditorStore';
import { ToolbarGroup, ToolbarSeparator } from 'containers/Tiptap/components/tiptap-ui-primitive/toolbar';
import { DocumentViewMode } from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';

import 'containers/Tiptap/components/tiptap-node/code-block-node/code-block-node.scss';
import 'containers/Tiptap/components/tiptap-node/list-node/list-node.scss';
import 'containers/Tiptap/components/tiptap-node/paragraph-node/paragraph-node.scss';

import { HeadingDropdownMenu } from 'containers/Tiptap/components/tiptap-ui/heading-dropdown-menu';
import { ListDropdownMenu } from 'containers/Tiptap/components/tiptap-ui/list-dropdown-menu';
import { MarkButton } from 'containers/Tiptap/components/tiptap-ui/mark-button';
import { TextAlignButton } from 'containers/Tiptap/components/tiptap-ui/text-align-button';

import { ToolbarContainer, ToolbarContent } from './styles';
import { UndoRedoButton } from '../undo-redo-button';
import ColorHighlightPopover from '../color-highlight-button/color-highlight-popover';
import { HIGHLIGHT_COLORS } from '../color-highlight-button/use-color-highlight';
import { ColorTextToolbarPopover } from '../color-text-button/color-text-toolbar-popover';
import { ZoomControls } from '../zoom-controls';
import { InsertDropdownMenu } from '../insert-dropdown-menu';
import { TableButton } from '../table-button';
import { ImageUploadButton } from '../image-upload-button';
import { HeaderFooterPanel } from '../header-footer';

const MainToolbarContent = observer((): React.ReactNode => {
  const { editor: bodyEditor } = useTiptapEditor();
  const editorStore = useEditorStore();

  // While a header/footer is edited inline, drive the toolbar from that overlay
  // editor instead of the (now read-only) body.
  const overlayEditor = useHeaderFooterOverlayEditor(bodyEditor);
  const editor = overlayEditor ?? bodyEditor;

  if (!editor || !editor.isEditable) {
    return null;
  }

  const showZoom = editorStore.viewMode === DocumentViewMode.Pages;

  return (
    <EditorContext.Provider value={{ editor }}>
      <ToolbarContainer data-tiptap-toolbar="true">
        <ToolbarContent>
          <ToolbarGroup>
            <UndoRedoButton action="undo" />
            <UndoRedoButton action="redo" />
          </ToolbarGroup>
          <ToolbarSeparator />
          {showZoom && (
            <>
              <ToolbarGroup>
                <EditorContext.Provider value={{ editor: bodyEditor }}>
                  <ZoomControls />
                </EditorContext.Provider>
              </ToolbarGroup>
              <ToolbarSeparator />
            </>
          )}
          <ToolbarGroup>
            <HeadingDropdownMenu levels={[1, 2, 3, 4, 5, 6]} />
            <ListDropdownMenu types={['bulletList', 'orderedList', 'taskList']} />
            <ColorTextToolbarPopover />
            <ColorHighlightPopover colors={HIGHLIGHT_COLORS} />
          </ToolbarGroup>
          <ToolbarSeparator />
          <ToolbarGroup>
            <MarkButton type="bold" />
            <MarkButton type="italic" />
            <MarkButton type="strike" />
            <MarkButton type="underline" />
          </ToolbarGroup>
          <ToolbarSeparator />
          <ToolbarGroup>
            <TextAlignButton align="left" />
            <TextAlignButton align="center" />
            <TextAlignButton align="right" />
            <TextAlignButton align="justify" />
          </ToolbarGroup>
          {!overlayEditor && (
            <>
              <ToolbarSeparator />
              <ToolbarGroup>
                <TableButton />
                <ImageUploadButton>
                  <AttachFileIcon className="tiptap-button-icon" fontSize="small" />
                </ImageUploadButton>
                <InsertDropdownMenu />
              </ToolbarGroup>
            </>
          )}
        </ToolbarContent>
        {bodyEditor && editorStore.viewMode === DocumentViewMode.Pages && <HeaderFooterPanel editor={bodyEditor} />}
      </ToolbarContainer>
    </EditorContext.Provider>
  );
});

export default MainToolbarContent;
